import { Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { PhotoUpload, ShowcaseStats, QrDownloads } from "@/components/ShowcaseExtras";

export const showcaseUrl = (handle) => `${window.location.origin}/learner/${handle}`;

export function ShowcaseSettings({ form, setForm, savedUser, setUser }) {
  const live = savedUser.showcase_public && savedUser.handle;
  const copy = async () => {
    try { await navigator.clipboard.writeText(showcaseUrl(savedUser.handle)); toast.success("Showcase link copied"); } catch (_) { toast.info(showcaseUrl(savedUser.handle)); }
  };
  return (
    <div data-testid="showcase-settings" className="flex flex-col gap-4 rounded-lg border border-line p-4">
      <label className="flex items-start gap-3">
        <Switch data-testid="profile-showcase-toggle" checked={form.showcase_public} onCheckedChange={(v) => setForm({ ...form, showcase_public: v })} />
        <span><span className="block text-sm font-medium text-ink">Public certificate showcase</span><span className="text-xs text-ink-3">A public page for employers listing your verified IDDCR certificates. Off by default; turn it off any time.</span></span>
      </label>
      {form.showcase_public && (
        <div className="flex flex-col gap-4 pl-1">
          {live && <ShowcaseStats />}
          <PhotoUpload user={savedUser} setUser={setUser} />
          <label className="flex items-start gap-3 rounded-md bg-canvas-2 p-3">
            <Switch data-testid="profile-allow-contact-toggle" checked={form.allow_contact} onCheckedChange={(v) => setForm({ ...form, allow_contact: v })} />
            <span><span className="block text-sm font-medium text-ink">Allow employers to contact me</span><span className="text-xs text-ink-3">Adds a "Contact this learner" button to your showcase. Messages are listed under Messages; your email stays private.</span></span>
          </label>
          {form.allow_contact && (
            <div data-testid="profile-message-alerts" className="-mt-2 ml-1 rounded-md border border-line p-3">
              <p className="mb-2 text-xs font-medium text-ink-2">How should we notify you about new messages?</p>
              <RadioGroup value={form.message_alerts} onValueChange={(v) => setForm({ ...form, message_alerts: v })} className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm text-ink-2"><RadioGroupItem value="email" data-testid="message-alerts-email" />Email + in-app</label>
                <label className="flex items-center gap-2 text-sm text-ink-2"><RadioGroupItem value="in_app" data-testid="message-alerts-in-app" />In-app only</label>
              </RadioGroup>
            </div>
          )}
          <div>
            <p className="mb-1 text-xs font-medium text-ink-2">Profile link</p>
            <div className="flex items-center rounded-md border border-line bg-canvas-2 pl-3 text-sm text-ink-3 focus-within:ring-2 focus-within:ring-aqua">
              <span className="whitespace-nowrap">/learner/</span>
              <Input data-testid="profile-handle-input" value={form.handle} placeholder="auto-created from your name"
                onChange={(e) => setForm({ ...form, handle: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                className="border-0 bg-transparent pl-0.5 shadow-none focus-visible:ring-0" maxLength={40} />
            </div>
            <p className="mt-1 text-xs text-ink-3">3-40 characters: lowercase letters, numbers and hyphens.</p>
          </div>
          <div>
            <p className="mb-1 text-xs font-medium text-ink-2">Headline (optional)</p>
            <Input data-testid="profile-headline-input" value={form.headline} maxLength={80} placeholder="e.g. Aspiring CDM Associate" onChange={(e) => setForm({ ...form, headline: e.target.value })} />
          </div>
          <div>
            <p className="mb-1 flex justify-between text-xs font-medium text-ink-2"><span>Short bio (optional)</span><span data-testid="profile-bio-count" className="font-normal text-ink-3">{form.bio.length}/300</span></p>
            <Textarea data-testid="profile-bio-input" value={form.bio} maxLength={300} rows={3} placeholder="A sentence or two about your background and the roles you're aiming for." onChange={(e) => setForm({ ...form, bio: e.target.value })} />
          </div>
          {live && (
            <div data-testid="showcase-live" className="flex flex-wrap items-center gap-2 rounded-md bg-aqua-soft px-3 py-2 text-xs text-aqua">
              <span className="font-medium">Live at /learner/{savedUser.handle}</span>
              <Button size="sm" variant="ghost" data-testid="showcase-copy-btn" onClick={copy} className="h-7 rounded-full"><Copy className="mr-1 h-3.5 w-3.5" />Copy</Button>
              <Button size="sm" variant="ghost" asChild className="h-7 rounded-full"><a data-testid="showcase-open-link" href={`/learner/${savedUser.handle}`} target="_blank" rel="noreferrer"><ExternalLink className="mr-1 h-3.5 w-3.5" />Open</a></Button>
            </div>
          )}
          {live && <QrDownloads handle={savedUser.handle} />}
        </div>
      )}
    </div>
  );
}
